import { PageSize } from '@/constant';
import {
	GetCommonNoticeListParams,
	ICommonNotice,
	TPayloadUpdateSuccess,
} from '@/services';
import { mergeItemUpdated, mergeListItemUpdated } from '@/utils';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from '..';
import { formatCommonNoticeReceiveList } from './common-notice-receive.helper';
import {
	ICommonNoticeReceiveDetailStore,
	IPayloadDeleteCommonNoticeReceiveRequest,
	IPayloadGetCommonNoticeReceiveDetailRequest,
	IPayloadGetCommonNoticeReceiveDetailSuccess,
	IPayloadGetCommonNoticeReceiveListRequest,
	IPayloadGetCommonNoticeReceiveListSuccess,
} from './common-notice-receive.type';

const InitParams: GetCommonNoticeListParams = {
	page: 1,
	pageSize: PageSize.S,
	searchTypes: [],
};

const initialState = {
	fetching: false,
	list: [] as ICommonNoticeReceiveDetailStore[],
	total: 0,
	params: InitParams,
	detail: {} as ICommonNoticeReceiveDetailStore,
};

const commonNoticeReceiveSlice = createSlice({
	initialState,
	name: 'commonNoticeReceive',
	reducers: {
		/**---- GET COMMON NOTICE RECEIVE LIST START ---- */
		getCommonNoticeReceiveListRequest: (
			state,
			action: PayloadAction<IPayloadGetCommonNoticeReceiveListRequest>
		) => {
			return {
				...state,
				fetching: true,
				params: action?.payload,
			};
		},
		getCommonNoticeReceiveListSuccess: (
			state,
			action: PayloadAction<IPayloadGetCommonNoticeReceiveListSuccess>
		) => {
			const { content, total } = action.payload;
			state.fetching = false;
			state.list = content;
			state.total = total;
		},
		getCommonNoticeReceiveListFailure: (state) => {
			state.fetching = false;
			state.list = [];
			state.total = 0;
		},
		/**---- GET COMMON NOTICE RECEIVE LIST END ---- */

		/**---- DELETE COMMON NOTICE RECEIVE START ---- */
		deleteCommonNoticeReceiveRequest: (
			state,
			action: PayloadAction<IPayloadDeleteCommonNoticeReceiveRequest>
		) => {
			state.fetching = true;
		},
		deleteCommonNoticeReceiveSuccess: (state) => {
			state.fetching = false;
		},
		deleteCommonNoticeReceiveFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE COMMON NOTICE RECEIVE START ---- */

		/**---- GET COMMON NOTICE RECEIVE DETAIL START ---- */
		getCommonNoticeReceiveDetailRequest: (
			state,
			action: PayloadAction<IPayloadGetCommonNoticeReceiveDetailRequest>
		) => {
			state.fetching = true;
		},
		getCommonNoticeReceiveDetailSuccess: (
			state,
			action: PayloadAction<IPayloadGetCommonNoticeReceiveDetailSuccess>
		) => {
			const { detail } = action.payload;
			state.fetching = false;
			state.detail = detail;
		},
		getCommonNoticeReceiveDetailFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET COMMON NOTICE RECEIVE DETAIL END ---- */

		/**---- UPDATE COMMON NOTICE RECEIVE START ---- */
		updateCommonNoticeReceiveListSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<ICommonNotice>>
		) => {
			const { data } = action.payload;
			const list = [...state.list];
			state.list = formatCommonNoticeReceiveList(
				mergeListItemUpdated(list, data)
			);
		},
		updateCommonNoticeReceiveDetailSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<ICommonNotice>>
		) => {
			const { data } = action.payload;
			const detail = { ...state.detail };
			state.detail = mergeItemUpdated<
				ICommonNoticeReceiveDetailStore,
				ICommonNotice
			>(detail, data);
		},
		/**---- UPDATE COMMON NOTICE RECEIVE END ---- */
	},
});

export const CommonNoticeReceiveSelectors = {
	selectFetching: (state: RootState) => state.commonNoticeReceive.fetching,
	selectParams: (state: RootState) => state.commonNoticeReceive.params,
	selectList: (state: RootState) => state.commonNoticeReceive.list,
	selectTotal: (state: RootState) => state.commonNoticeReceive.total,
	selectDetail: (state: RootState) => state.commonNoticeReceive.detail,
};

export const CommonNoticeReceiveActions = commonNoticeReceiveSlice.actions;
export const CommonNoticeReceiveReducer = commonNoticeReceiveSlice.reducer;
